import React, { useEffect, useState } from "react";
import PayoutAttempts from "../../../components/PayoutAttempts";
import { Search, X, Copy } from "lucide-react";
import { toast } from "react-hot-toast";
import Pagination from "../../../components/ui/Pagination";
import { useFailedPayouts } from "../hooks/useFailedPayouts";
import { realCause, customerMessage, causeNote, handledByProvider } from "../failureCause";

const statusStyle = {
  refunded: "bg-blue-100 text-blue-800",
  failed: "bg-red-100 text-red-800",
};

const copy = (text) => {
  if (!text) return;
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
};

const Field = ({ label, value, copyable }) => (
  <div className="flex flex-col mb-4">
    <span className="text-xs tracking-wider text-gray-500 uppercase">{label}</span>
    <span className="flex gap-2 items-center text-sm font-medium text-gray-900 break-all">
      {value || "N/A"}
      {copyable && value ? (
        <button onClick={() => copy(value)} className="text-gray-500 hover:text-black" aria-label={`Copy ${label}`}>
          <Copy size={14} />
        </button>
      ) : null}
    </span>
  </div>
);

const DetailsModal = ({ payout, onClose }) => {
  if (!payout) return null;
  const m = payout.metadata || {};
  const symbol = payout.currency?.symbol || "";
  const money = (v) => `${symbol} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="overflow-hidden mx-4 w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh]">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Why this payout failed</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 max-h-[75vh]">
          <div className="p-4 mb-6 bg-red-50 rounded-xl border border-red-100">
            <span className="text-xs tracking-wider text-red-700 uppercase">Real cause · {causeNote(payout)}</span>
            <p className="mt-1 text-sm font-medium text-gray-900">{realCause(payout) || "No cause was recorded for this payout."}</p>
            {m.provider_failure_code ? <p className="mt-1 text-xs text-gray-600">Partner code: {String(m.provider_failure_code)}</p> : null}
          </div>
          <div className="p-4 mb-6 bg-gray-50 rounded-xl border">
            <span className="text-xs tracking-wider text-gray-500 uppercase">Customer was told</span>
            <p className="mt-1 text-sm text-gray-900">{customerMessage(payout) || "N/A"}</p>
            {payout.statusReason ? <p className="mt-1 text-xs text-gray-500">Category: {payout.statusReason}</p> : null}
          </div>

          <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <Field label="Status" value={payout.status?.toUpperCase()} />
            <Field label="Date" value={payout.createdAt ? new Date(payout.createdAt).toLocaleString() : null} />
            <Field label="Amount" value={money(payout.amount)} />
            <Field label="Total debited" value={money(payout.totalAmount)} />
            <Field label="Customer" value={`${payout.user?.firstName || ""} ${payout.user?.lastName || ""}`.trim()} />
            <Field label="Email" value={payout.user?.email} />
            <Field label="Recipient" value={payout.beneficiary?.name} />
            <Field label="Payout method" value={payout.payoutMethod?.name} />
            <Field label="Handled by" value={handledByProvider(payout) || "Never reached a partner"} />
            <Field label="Partner status" value={payout.handledBy?.status} />
            <Field label="Payout ID" value={payout.uuid} copyable />
            <Field label="Transfer reference" value={payout.transferReference} copyable />
            <Field label="Partner reference" value={payout.handledBy?.providerReference || payout.providerReference} copyable />
            <Field label="Failed at" value={(payout.handledBy?.failedAt || m.provider_failed_at) ? new Date(payout.handledBy?.failedAt || m.provider_failed_at).toLocaleString() : null} />
            <Field label="Last retry error" value={m.last_retry_error} />
            <Field label="Declined note" value={m.decline_reason} />
          </div>

          <PayoutAttempts payout={payout} />

          {payout.handledBy?.failureRaw ? (
            <div className="mt-2">
              <span className="text-xs tracking-wider text-gray-500 uppercase">What {handledByProvider(payout) || "the partner"} sent</span>
              <pre className="overflow-x-auto p-3 mt-1 text-xs bg-gray-900 text-gray-100 rounded-xl whitespace-pre-wrap break-all">
                {payout.handledBy.failureRaw}
              </pre>
            </div>
          ) : null}

          {m.provider_failure_event ? (
            <div className="mt-2">
              <span className="text-xs tracking-wider text-gray-500 uppercase">Partner webhook data</span>
              <pre className="overflow-x-auto p-3 mt-1 text-xs bg-gray-900 text-gray-100 rounded-xl">
                {JSON.stringify(m.provider_failure_event, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
        <div className="flex justify-end px-6 py-4 bg-gray-50 border-t">
          <button onClick={onClose} className="py-2 px-5 text-sm font-bold text-gray-600 bg-white rounded-xl border-2 border-gray-200 hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export function FailedPayoutTable() {
  const [filter, setFilter] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(filter);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [filter]);

  const { data, isLoading, isError, error } = useFailedPayouts(page, limit, debounced);
  const rows = data?.data || [];
  const totalPages = data?.pagination?.totalPages || data?.meta?.totalPages || 1;

  return (
    <div>
      <div className="flex relative items-center mb-4 max-w-md">
        <Search className="absolute left-3 w-4 h-4 text-gray-400" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by customer, recipient or reference"
          className="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-500 uppercase bg-gray-50">
            <tr>
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-4">Amount</th>
              <th className="py-3 px-4">Partner</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Real cause</th>
              <th className="py-3 px-4">Customer was told</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="p-6 text-center text-gray-500">Loading…</td></tr>
            ) : isError ? (
              <tr><td colSpan={8} className="p-6 text-center text-red-600">{error?.response?.data?.message || "Could not load payouts"}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-gray-500">No failed or refunded payouts.</td></tr>
            ) : (
              rows.map((p) => (
                <tr key={p.uuid} className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4 whitespace-nowrap">{p.createdAt ? new Date(p.createdAt).toLocaleString() : "N/A"}</td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">{p.user?.firstName} {p.user?.lastName}</div>
                    <div className="text-xs text-gray-500">{p.user?.email}</div>
                  </td>
                  <td className="py-3 px-4 font-semibold whitespace-nowrap">
                    {p.currency?.symbol || ""} {Number(p.totalAmount || p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    {handledByProvider(p) || <span className="text-gray-400">None</span>}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusStyle[p.status] || "bg-gray-100 text-gray-800"}`}>
                      {p.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4 max-w-xs">
                    <div className="line-clamp-2" title={realCause(p) || ""}>{realCause(p) || <span className="text-gray-400">Not recorded</span>}</div>
                    <div className="text-xs text-gray-400">{causeNote(p)}</div>
                  </td>
                  <td className="py-3 px-4 max-w-xs text-gray-600"><div className="line-clamp-2" title={customerMessage(p) || ""}>{customerMessage(p) || "N/A"}</div></td>
                  <td className="py-3 px-4">
                    <button onClick={() => setSelected(p)} className="py-1 px-3 text-xs font-bold text-emerald-700 rounded-lg border border-emerald-500 hover:bg-emerald-50">
                      Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <DetailsModal payout={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
